import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
// import { Separator } from './ui/separator'; // Make sure separator exists or use <div className="h-[1px] bg-border my-6" />
import {
    User,
    Lightbulb,
    Database,
    Cpu,
    BarChart3,
    Mail,
    ShieldCheck,
    Wrench,
    Info,
    Contact,
    Globe
} from 'lucide-react';

export default function Documentation() {
    return (
        <div className="min-h-screen bg-muted/10 py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-8">

                <div className="text-center space-y-2">
                    <h1 className="text-4xl font-extrabold tracking-tight lg:text-5xl text-primary">
                        Mailwise Documentation
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        Complete guide to the Mailwise automated job application system.
                    </p>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <User className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <CardTitle>Who Am I?</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="leading-7">
                            I am Ömer Faruk Yılmaz, 20 years old, and I have been developing myself in the software field since middle school.
                            I develop applications with modern technologies like React, React Native, and Node.js, and I constantly strive to
                            progress in the professional field. I am currently working as a frontend developer at a startup and
                            continue to create with my individual projects.
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Lightbulb className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Why Did I Make This Project?</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="leading-7">
                            The main purpose of developing Mailwise was to introduce my software career to more companies and to make the
                            classic job application process more effective.
                        </p>
                        <p className="leading-7">
                            In Turkey, the recruitment processes in the software field are generally passive and ad-oriented.
                            With this system, I am reversing this situation:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li>
                                I want to reach not only the companies with open positions but also those that may potentially be looking for developers.
                            </li>
                            <li>
                                Instead of visiting each company's career page one by one, I reach the relevant people directly
                                with personalized bulk emails.
                            </li>
                        </ul>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Database className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Where Do I Find the Data?</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="leading-7">
                            All contact information and company data used in the system are obtained from apollo.io.
                            Thanks to this platform:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li>Name, surname, e-mail address, and title of the authorized persons</li>
                            <li>Company name, address, industry, and website</li>
                            <li>Personal and corporate LinkedIn connections</li>
                        </ul>
                        <div className="bg-muted p-4 rounded-lg mt-4 border-l-4 border-primary">
                            <p className="text-sm">
                                📌 <strong>Note:</strong> All data is collected from publicly available sources and is used for professional communication purposes only.
                                It does not have any commercial advertising, spam, or personal data violation purposes.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Cpu className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>How Does the System Work?</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                            <li>Personal and company information obtained from Apollo.io is saved to the database.</li>
                            <li>Dynamic e-mail templates are created for each person (name, company, position, etc. information is added).</li>
                            <li>These e-mails are sent via a mail service determined by the user (e.g., Gmail, SMTP, etc.).</li>
                            <li>The status of the sent e-mails can be tracked instantly from the system panel.</li>
                        </ol>
                    </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center gap-4">
                            <div className="p-2 bg-primary/10 rounded-full">
                                <BarChart3 className="h-6 w-6 text-primary" />
                            </div>
                            <CardTitle>What Information Can Be Viewed?</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">• Total number of e-mails sent</li>
                                <li className="flex items-center gap-2">• How many people were sent to</li>
                                <li className="flex items-center gap-2">• Status (Successful, Pending, Error)</li>
                                <li className="flex items-center gap-2">• CV sending history</li>
                                <li className="flex items-center gap-2">• Daily e-mail sending graph</li>
                                <li className="flex items-center gap-2">• (Soon) Mail open and click rates</li>
                            </ul>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center gap-4">
                            <div className="p-2 bg-primary/10 rounded-full">
                                <Mail className="h-6 w-6 text-primary" />
                            </div>
                            <CardTitle>E-mail Templates</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">• Auto-filled company & recipient info</li>
                                <li className="flex items-center gap-2">• Professional introduction text</li>
                                <li className="flex items-center gap-2">• CV attached automatically (PDF)</li>
                                <li className="flex items-center gap-2">• Flexible & customizable templates</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <ShieldCheck className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Privacy Policy & Ethical Approach</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li>There is no bulk sending of spam or advertising content.</li>
                            <li>Each e-mail is created specifically for the recipient and a respectful language is used.</li>
                            <li>If desired, an opt-out option can be provided (in a future version).</li>
                            <li>All data is used only for the individual application process.</li>
                        </ul>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Wrench className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Future Plans</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="secondary">📈 Mail Analytics</Badge>
                            <Badge variant="secondary">🔁 Auto Follow-up</Badge>
                            <Badge variant="secondary">🔗 LinkedIn Integration</Badge>
                            <Badge variant="secondary">📋 View Tracking</Badge>
                            <Badge variant="secondary">🧑‍💼 Multi-user Support</Badge>
                            <Badge variant="secondary">📤 CSV Import/Export</Badge>
                            <Badge variant="secondary">⚙️ Advanced Settings</Badge>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Info className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Tech Stack</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div className="flex flex-col">
                                <span className="font-semibold">Frontend</span>
                                <span className="text-muted-foreground">React, Tailwind, Shadcn UI</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="font-semibold">Backend</span>
                                <span className="text-muted-foreground">Node.js, Express, MongoDB</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="font-semibold">Services</span>
                                <span className="text-muted-foreground">Nodemailer, Apollo.io, Worker Threads</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-primary/5 border-primary/20">
                    <CardHeader className="flex flex-row items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                            <Contact className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle>Contact</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Ömer Faruk Yılmaz</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <a href="mailto:omer@omerfarukyilmaz.dev" className="text-primary hover:underline">omer@omerfarukyilmaz.dev</a>
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <a href="https://omerfarukyilmaz.dev" target="_blank" rel="noreferrer" className="text-primary hover:underline">omerfarukyilmaz.dev</a>
                        </div>
                    </CardContent>
                </Card>

            </div>
        </div>
    );
} 